import React, { useEffect } from "react";
import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { findService, services } from "@/data/services";
import { Button } from "@/components/ui/button";
import {
  ArrowUpRight,
  ArrowLeft,
  Check,
  ChevronRight,
} from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = findService(slug);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
    if (service) {
      document.title = `${service.title} · Central Solar | New Plymouth, NZ`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", service.short);
    }
    return () => {
      document.title = "Central Solar | Solar Panel Installers in New Plymouth & Taranaki, NZ";
    };
  }, [slug, service]);

  if (!service) return <Navigate to="/" replace />;

  const Icon = service.icon;
  const others = services.filter((s) => s.slug !== service.slug).slice(0, 3);

  const goQuote = () => navigate("/#quote");

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid={`service-page-${service.slug}`}>
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden bg-[#1A3626] text-white">
        <div className="absolute inset-0 -z-10">
          <img
            src={service.image}
            alt=""
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F2117] via-[#1A3626]/95 to-[#1A3626]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(245,194,66,0.20),transparent_55%)]" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            data-testid="service-back-home"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#F5C242] hover:text-white transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to home
          </Link>

          <div className="mt-8 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 text-xs uppercase tracking-[0.25em]">
              <Icon className="h-3.5 w-3.5 text-[#F5C242]" />
              {service.eyebrow}
            </div>
            <h1
              data-testid="service-title"
              className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-medium mt-7 leading-[1.02]"
            >
              {service.headline}
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-2xl leading-relaxed">
              {service.intro}
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Button
                data-testid="service-cta-quote"
                onClick={goQuote}
                className="rounded-full bg-[#F5C242] text-[#1A3626] hover:bg-[#DEAA2B] h-14 px-8 text-base group"
              >
                Get a quote for {service.title.toLowerCase()}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Button>
              <Link
                to="/"
                data-testid="service-cta-all"
                className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-white/80 hover:text-white self-center"
              >
                View all services
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-[#5C6B61]">
            · What&rsquo;s included
          </div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-medium mt-4 text-[#1A3626] max-w-2xl">
            What makes our {service.title.toLowerCase()} different.
          </h2>

          <div className="mt-14 grid md:grid-cols-2 gap-5">
            {service.highlights.map((h) => (
              <div
                key={h.h}
                data-testid={`service-highlight-${h.h.replace(/\s+/g, "-").toLowerCase()}`}
                className="rounded-3xl bg-white border border-[#D2D7D0] p-8 hover:-translate-y-1 transition-transform duration-500"
                style={{ boxShadow: "0 8px 32px rgba(26,54,38,0.05)" }}
              >
                <div className="flex items-start gap-4">
                  <span className="h-10 w-10 rounded-2xl bg-[#1A3626] text-[#F5C242] flex items-center justify-center shrink-0">
                    <Check className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="font-display text-xl text-[#1A3626]">{h.h}</h3>
                    <p className="mt-2 text-sm text-[#5C6B61] leading-relaxed">{h.d}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process strip */}
      <section className="py-16 lg:py-24 bg-[#E8EAE6]/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-[#5C6B61]">
            · The process
          </div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium mt-4 text-[#1A3626]">
            How we deliver this service.
          </h2>

          <ol className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {service.process.map((step, i) => (
              <li
                key={step}
                data-testid={`service-step-${i + 1}`}
                className="relative rounded-2xl bg-white border border-[#D2D7D0] p-5"
              >
                <div className="font-display text-2xl text-[#F5C242]">0{i + 1}</div>
                <div className="mt-3 text-sm text-[#1A3626] leading-snug">{step}</div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-[#5C6B61]">
            · Common questions
          </div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium mt-4 text-[#1A3626]">
            Frequently asked.
          </h2>

          <div className="mt-10 space-y-4">
            {service.faqs.map((f, i) => (
              <details
                key={f.q}
                data-testid={`service-faq-${i + 1}`}
                className="group rounded-2xl bg-white border border-[#D2D7D0] p-6 open:border-[#1A3626]/40"
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                  <span className="font-display text-lg text-[#1A3626]">{f.q}</span>
                  <ChevronRight className="h-5 w-5 text-[#5C6B61] mt-1 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-4 text-sm text-[#5C6B61] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Other services */}
      <section className="py-20 lg:py-28 bg-[#E8EAE6]/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#5C6B61]">
                · Keep exploring
              </div>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium mt-4 text-[#1A3626]">
                Other services we offer
              </h2>
            </div>
            <Link
              to="/"
              data-testid="service-back-all"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[#1A3626] hover:text-[#234B35]"
            >
              See all <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {others.map((o) => {
              const OIcon = o.icon;
              return (
                <Link
                  key={o.slug}
                  to={`/services/${o.slug}`}
                  data-testid={`service-card-other-${o.slug}`}
                  className="group rounded-3xl bg-white border border-[#D2D7D0] p-7 hover:-translate-y-1 hover:border-[#1A3626]/40 transition-all duration-500"
                  style={{ boxShadow: "0 8px 32px rgba(26,54,38,0.05)" }}
                >
                  <span className="h-10 w-10 rounded-2xl bg-[#E8EAE6] group-hover:bg-[#F5C242] text-[#1A3626] flex items-center justify-center transition-colors">
                    <OIcon className="h-5 w-5" />
                  </span>
                  <h3 className="font-display text-xl mt-6 text-[#1A3626]">{o.title}</h3>
                  <p className="mt-3 text-sm text-[#5C6B61] leading-relaxed">{o.short}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#1A3626]">
                    Learn more
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#1A3626] text-white text-center">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-medium leading-[1.05]">
            Ready to talk {service.title.toLowerCase()}?
          </h2>
          <p className="mt-5 text-white/75">
            Get a free, no-obligation quote within one business day.
          </p>
          <Button
            data-testid="service-cta-quote-footer"
            onClick={goQuote}
            className="mt-10 rounded-full bg-[#F5C242] text-[#1A3626] hover:bg-[#DEAA2B] h-14 px-8 text-base group"
          >
            Request a quote
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceDetail;
